import { colors as tokenColors } from "@plasma/tokens/theme";

/**
 * Stripe Connect Theme Configuration
 *
 * Centralized theme for all Stripe Connect embedded components.
 * Mirrors Plasma's design system colors.
 *
 * NOTE: Unlike the old auth UI, Stripe Connect does NOT support CSS variables.
 * We must provide hex values and use their update() API for theme switching.
 *
 * Full appearance options reference:
 * https://docs.stripe.com/connect/embedded-appearance-options
 */

// Light mode
const light = {
  background: tokenColors.light.background,
  foreground: tokenColors.light.foreground,
  card: tokenColors.light.card,
  muted: tokenColors.light.muted,
  mutedForeground: tokenColors.light.mutedForeground,
  border: tokenColors.light.border,
  primary: tokenColors.light.primary,
  primaryForeground: tokenColors.light.primaryForeground,
  destructive: tokenColors.light.destructive,
  accent: tokenColors.light.primary, // For form accents (checkboxes, radio buttons)
} as const;

// Dark mode
const dark = {
  background: tokenColors.dark.background,
  foreground: tokenColors.dark.foreground,
  card: tokenColors.dark.card,
  muted: tokenColors.dark.muted,
  mutedForeground: tokenColors.dark.mutedForeground,
  border: tokenColors.dark.border,
  primary: tokenColors.dark.primary,
  primaryForeground: tokenColors.dark.primaryForeground,
  destructive: tokenColors.dark.destructive,
  accent: tokenColors.dark.primary, // Lighter blue for dark mode
} as const;

/**
 * Stripe Connect appearance configuration
 * Using ALL available variables for maximum customization
 */
export function getStripeAppearance(isDark: boolean) {
  const colors = isDark ? dark : light;

  return {
    overlays: "dialog" as const,
    variables: {
      // ============================================
      // CORE COLORS
      // ============================================
      colorPrimary: colors.primary,
      colorBackground: colors.background,
      colorText: colors.foreground,
      colorSecondaryText: colors.mutedForeground,
      colorBorder: colors.border,
      colorDanger: colors.destructive,

      // ============================================
      // FORM ELEMENTS
      // ============================================
      formBackgroundColor: colors.card,
      formAccentColor: colors.accent, // Checkboxes, radio buttons, switches

      // ============================================
      // SECONDARY/OFFSET BACKGROUNDS
      // ============================================
      offsetBackgroundColor: colors.muted,

      // ============================================
      // BUTTONS
      // ============================================
      buttonPrimaryColorBackground: colors.primary,
      buttonPrimaryColorText: colors.primaryForeground,
      buttonSecondaryColorBackground: isDark
        ? tokenColors.dark.input
        : tokenColors.light.input,
      buttonSecondaryColorText: colors.foreground,
      buttonBorderRadius: "8px", // Slightly smaller than container radius

      // ============================================
      // BORDER RADIUS (granular control)
      // ============================================
      borderRadius: "12px", // General/container radius
      formBorderRadius: "8px", // Form inputs
      badgeBorderRadius: "6px", // Status badges
      overlayBorderRadius: "16px", // Modals/dialogs - larger for emphasis

      // ============================================
      // OVERLAY/Z-INDEX (fixes layering issues!)
      // ============================================
      overlayBackdropColor: isDark ? "rgba(0,0,0,0.8)" : "rgba(0,0,0,0.5)",
      overlayZIndex: 9999, // High z-index to ensure overlays appear above everything

      // ============================================
      // TYPOGRAPHY - Match Plasma's Geist font
      // ============================================
      fontFamily: '"Geist", "Inter", system-ui, -apple-system, sans-serif',
      fontSizeBase: "14px",

      // Body typography
      bodyMdFontSize: "14px",
      bodyMdFontWeight: "400",
      bodySmFontSize: "12px",
      bodySmFontWeight: "400",

      // Heading typography (match Plasma's font-heading)
      headingXlFontSize: "24px",
      headingXlFontWeight: "600",
      headingXlTextTransform: "none" as const,
      headingLgFontSize: "20px",
      headingLgFontWeight: "600",
      headingLgTextTransform: "none" as const,
      headingMdFontSize: "16px",
      headingMdFontWeight: "600",
      headingMdTextTransform: "none" as const,
      headingSmFontSize: "14px",
      headingSmFontWeight: "600",
      headingSmTextTransform: "none" as const,
      headingXsFontSize: "12px",
      headingXsFontWeight: "600",
      headingXsTextTransform: "none" as const,

      // Label typography
      labelMdFontSize: "14px",
      labelMdFontWeight: "500",
      labelMdTextTransform: "none" as const,
      labelSmFontSize: "12px",
      labelSmFontWeight: "500",
      labelSmTextTransform: "none" as const,

      // ============================================
      // SPACING
      // ============================================
      spacingUnit: "8px", // Base unit for all spacing calculations (min 8px, max 20px)
    },
  };
}

/**
 * Light theme appearance (convenience export)
 */
export const stripeAppearanceLight = getStripeAppearance(false);

/**
 * Dark theme appearance (convenience export)
 */
export const stripeAppearanceDark = getStripeAppearance(true);

/**
 * Type for the appearance object (useful for the update API)
 */
export type StripeAppearance = ReturnType<typeof getStripeAppearance>;
